import React, { useState } from 'react';
import { purgeAllQuestionsAndTestCases, purgeAllUserData, purgeAllData } from '../../lib/adminApi';

function AdminSuperSettings() {
  const [loading, setLoading] = useState(null); // 'questions' | 'userdata' | 'all' | null
  const [result, setResult] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'questions' | 'userdata' | 'all' }
  const [confirmText, setConfirmText] = useState('');

  const handlePurgeQuestions = async () => {
    setLoading('questions');
    setResult(null);
    try {
      const data = await purgeAllQuestionsAndTestCases();
      setResult({
        type: 'success',
        action: 'questions',
        data,
      });
    } catch (err) {
      setResult({
        type: 'error',
        message: err.message || 'Failed to purge questions',
      });
    } finally {
      setLoading(null);
      setConfirmAction(null);
      setConfirmText('');
    }
  };

  const handlePurgeUserData = async () => {
    setLoading('userdata');
    setResult(null);
    try {
      const data = await purgeAllUserData();
      setResult({
        type: 'success',
        action: 'userdata',
        data,
      });
    } catch (err) {
      setResult({
        type: 'error',
        message: err.message || 'Failed to purge user data',
      });
    } finally {
      setLoading(null);
      setConfirmAction(null);
      setConfirmText('');
    }
  };

  const handlePurgeAll = async () => {
    setLoading('all');
    setResult(null);
    try {
      const data = await purgeAllData();
      setResult({
        type: 'success',
        action: 'all',
        data,
      });
    } catch (err) {
      setResult({
        type: 'error',
        message: err.message || 'Failed to purge all data',
      });
    } finally {
      setLoading(null);
      setConfirmAction(null);
      setConfirmText('');
    }
  };

  const confirmRequired = confirmAction?.type === 'all' ? 'DELETE ALL' : 'DELETE';
  const isConfirmValid = confirmText === confirmRequired;

  const handleConfirm = () => {
    if (!isConfirmValid) return;
    if (confirmAction?.type === 'questions') {
      handlePurgeQuestions();
    } else if (confirmAction?.type === 'userdata') {
      handlePurgeUserData();
    } else if (confirmAction?.type === 'all') {
      handlePurgeAll();
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Super Admin Settings</h1>
          <p className="text-gray-400 text-sm mt-1">Dangerous operations - use with caution</p>
        </div>
      </div>

      {/* Result Display */}
      {result && (
        <div className={`mb-6 p-4 rounded-lg border ${
          result.type === 'success'
            ? 'bg-green-900/20 border-green-600/50'
            : 'bg-red-900/20 border-red-600/50'
        }`}>
          {result.type === 'success' ? (
            <div>
              <h3 className="text-green-400 font-medium mb-2">Operation Completed Successfully</h3>
              <div className="text-gray-300 text-sm space-y-1">
                {result.data.questionsDeleted !== undefined && (
                  <p>Questions deleted: <span className="text-white font-medium">{result.data.questionsDeleted}</span></p>
                )}
                {result.data.testCasesDeleted !== undefined && (
                  <p>Test cases deleted: <span className="text-white font-medium">{result.data.testCasesDeleted}</span></p>
                )}
                {result.data.mappingsDeleted !== undefined && (
                  <p>User mappings deleted: <span className="text-white font-medium">{result.data.mappingsDeleted}</span></p>
                )}
                {result.data.submissionsDeleted !== undefined && (
                  <p>Submissions deleted: <span className="text-white font-medium">{result.data.submissionsDeleted}</span></p>
                )}
                {result.data.activityLogsDeleted !== undefined && (
                  <p>Activity logs deleted: <span className="text-white font-medium">{result.data.activityLogsDeleted}</span></p>
                )}
                {result.data.retryRequestsDeleted !== undefined && (
                  <p>Retry requests deleted: <span className="text-white font-medium">{result.data.retryRequestsDeleted}</span></p>
                )}
                {result.data.feedbacksDeleted !== undefined && (
                  <p>Feedbacks deleted: <span className="text-white font-medium">{result.data.feedbacksDeleted}</span></p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-red-400">{result.message}</p>
          )}
        </div>
      )}

      {/* Purge Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Purge Questions & Test Cases */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl">❓</span>
            <h2 className="text-lg font-semibold text-white">Questions & Test Cases</h2>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Delete all questions and their associated test cases from the database.
          </p>
          <div className="bg-yellow-900/20 border border-yellow-600/30 rounded p-3 mb-4">
            <p className="text-yellow-400 text-xs">
              This will remove all questions and test cases. User mappings will become orphaned.
            </p>
          </div>
          <button
            onClick={() => setConfirmAction({ type: 'questions' })}
            disabled={loading !== null}
            className="w-full px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading === 'questions' ? 'Deleting...' : 'Purge Questions'}
          </button>
        </div>

        {/* Purge User Data */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl">👥</span>
            <h2 className="text-lg font-semibold text-white">User Data</h2>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Delete all user progress data including mappings, submissions, activity logs, retry requests, and feedbacks.
          </p>
          <div className="bg-yellow-900/20 border border-yellow-600/30 rounded p-3 mb-4">
            <p className="text-yellow-400 text-xs">
              This will reset all user progress. Users will need to start fresh.
            </p>
          </div>
          <button
            onClick={() => setConfirmAction({ type: 'userdata' })}
            disabled={loading !== null}
            className="w-full px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading === 'userdata' ? 'Deleting...' : 'Purge User Data'}
          </button>
        </div>

        {/* Purge All Data */}
        <div className="bg-gray-800 rounded-lg p-6 border border-red-700">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl">💀</span>
            <h2 className="text-lg font-semibold text-red-400">COMPLETE RESET</h2>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Delete ALL data - questions, test cases, user mappings, submissions, activity logs, retry requests, and feedbacks.
          </p>
          <div className="bg-red-900/20 border border-red-600/30 rounded p-3 mb-4">
            <p className="text-red-400 text-xs font-medium">
              DANGER: This is a complete system reset. This action cannot be undone!
            </p>
          </div>
          <button
            onClick={() => setConfirmAction({ type: 'all' })}
            disabled={loading !== null}
            className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading === 'all' ? 'Deleting Everything...' : 'PURGE ALL DATA'}
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">
              Confirm {confirmAction.type === 'all' ? 'Complete Reset' : 'Deletion'}
            </h2>

            <div className={`p-4 rounded-lg mb-4 ${
              confirmAction.type === 'all'
                ? 'bg-red-900/30 border border-red-600/50'
                : 'bg-yellow-900/30 border border-yellow-600/50'
            }`}>
              {confirmAction.type === 'questions' && (
                <p className="text-yellow-300 text-sm">
                  You are about to delete <strong>ALL questions and test cases</strong>.
                  This action cannot be undone.
                </p>
              )}
              {confirmAction.type === 'userdata' && (
                <p className="text-yellow-300 text-sm">
                  You are about to delete <strong>ALL user data</strong> including mappings,
                  submissions, activity logs, retry requests, and feedbacks.
                  This action cannot be undone.
                </p>
              )}
              {confirmAction.type === 'all' && (
                <p className="text-red-300 text-sm">
                  You are about to perform a <strong>COMPLETE SYSTEM RESET</strong>.
                  ALL data will be permanently deleted. This action cannot be undone!
                </p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 text-sm mb-2">
                Type <span className="font-mono font-bold text-white">{confirmRequired}</span> to confirm:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={confirmRequired}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setConfirmAction(null);
                  setConfirmText('');
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!isConfirmValid || loading !== null}
                className={`px-4 py-2 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed ${
                  confirmAction.type === 'all'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-orange-600 hover:bg-orange-700'
                }`}
              >
                {loading ? 'Processing...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSuperSettings;
